import Button from 'components/UI/Button';
import { renderToast, updateToast, toast } from 'components/UI/ToastNotification';
import Vendor from 'data/Vendor';
import { navigate } from 'hooks/useHookRouterCompat';
import React, { useEffect } from 'react';
import Product, * as Prod from 'data/Product';
import Currency from 'data/Currency';
import Tooltip from 'components/UI/Tooltip';
import { RECOMMENDATION_STRINGS } from 'constants/productRecommendations';
import styled from 'styled-components';
import { TEXT_LABELS } from 'constants/productDetails';
import { Background, Shade } from 'styles/themeColors';
import { MAX_PRODUCT_NAME_LENGTH, PRODUCT_STATUS } from 'constants/product';
import { getProductTypeConfig } from 'utils/product';
import { useReadPermission } from 'hooks/useUserRoles';
import Country from 'data/Country';
import { getVariantsMissingRegionalPrice, getVariantsWithInvalidCommission } from 'utils/variant';
import { AFFILIATE_FORM_LABELS, AFFILIATE_FORM_TOAST_ID } from 'constants/affiliateForm';
import { PRODUCT_BADGE_INACTIVE } from 'constants/productBadges';

const TooltipContent = styled.div`
  text-align: left;
  white-space: initial;
  width: 15rem;
`;

interface Props {
  loading: boolean;
  detailsUrl: string;
  saveDisabled: boolean;
  setSaveDisabled: (value: boolean) => void;
  productDetails: Product | undefined;
  currentVendor: Vendor;
  token: string | null;
  currencies: Currency[];
  productErrorsCount: number;
  variantErrorsCount: number;
  /** True while a region on the Affiliate form tab is showing an invalid Cognito form id. */
  affiliateFormError?: boolean;
  /** True while a recommendation-section editor is open on the details tab. */
  recommendationEditorOpen?: boolean;
  setIsSubmitModalOpen: (value: boolean) => void;
  setValidation: (value: boolean) => void;
  view: string;
  keepWarehouse: boolean;
  setKeepWarehouse: (value: boolean) => void;
  loadProducts: () => void;
  loadProductVariants?: (resetPagination?: boolean) => void;
  warehouseLength?: number;
  disabled?: boolean;
  setLoading: (val: boolean) => void;
  setIsErrorHighlight: (val: boolean) => void;
  productType?: string;
  countries?: Country[];
}
export default function HeaderButtonComponent({
  loading,
  detailsUrl,
  saveDisabled,
  setSaveDisabled,
  productDetails,
  currentVendor,
  token,
  currencies,
  variantErrorsCount,
  productErrorsCount,
  affiliateFormError = false,
  recommendationEditorOpen = false,
  setIsSubmitModalOpen,
  setValidation,
  view,
  keepWarehouse,
  setKeepWarehouse,
  loadProducts,
  loadProductVariants,
  warehouseLength,
  disabled,
  setLoading,
  setIsErrorHighlight,
  productType,
  countries = []
}: Props) {

  const typeConfig = getProductTypeConfig(productType);
  const { readOnly: isSubmitReadOnly } = useReadPermission(typeConfig.permissions.submit);
  const isDraftStatus = productDetails?.status === PRODUCT_STATUS.DRAFT;


  const warehouseValidated = (productDetails: Product | undefined) => {
    const hasVariantsHavePurchaseType = productDetails?.variants.some((variant) => variant.purchase);
    const hasVariantsHaveRentType = productDetails?.variants.some((variant) => variant.rent);

    const purchaseWarehouseAdded = productDetails?.warehouses.some((warehouse) => warehouse.type === TEXT_LABELS.PURCHASE_C);
    const rentWarehouseAdded = productDetails?.warehouses.some((warehouse) => warehouse.type === TEXT_LABELS.RENT_C);
    const toastId = TEXT_LABELS.SAVE_PRODUCT;
    if ((hasVariantsHavePurchaseType && hasVariantsHaveRentType) && (!purchaseWarehouseAdded && !rentWarehouseAdded)) {
      renderToast(
        toast.TYPE.ERROR,
        TEXT_LABELS.BOTH_WAREHOUSE_ERROR,
        {
          toastId,
          autoClose: false,
        }
      );
      return false
    }
    if (hasVariantsHavePurchaseType && !purchaseWarehouseAdded) {
      renderToast(
        toast.TYPE.ERROR,
        TEXT_LABELS.PURCHASE_WAREHOUSE_ERROR,
        {
          toastId,
          autoClose: false,
        }
      );
      return false
    }

    if (hasVariantsHaveRentType && !rentWarehouseAdded) {
      renderToast(
        toast.TYPE.ERROR,
        TEXT_LABELS.RENT_WAREHOUSE_ERROR,
        {
          toastId,
          autoClose: false,
        }
      );
      return false
    }
    return true
  }

  const commissionPricesValidated = (productDetails: Product | undefined) => {
    if (!productDetails) return true;
    const toastId = TEXT_LABELS.SAVE_PRODUCT;

    for (const region of ['US', 'CA'] as const) {
      const missingSkus = getVariantsMissingRegionalPrice(productDetails.variants, region, countries, productDetails.warehouses);
      if (missingSkus.length > 0) {
        renderToast(
          toast.TYPE.ERROR,
          `Cannot save: ${missingSkus.join(', ')} ${missingSkus.length > 1 ? 'are' : 'is'} missing a ${region} purchase price required to calculate ${region} commission.`,
          {
            toastId,
            autoClose: false,
          }
        );
        return false;
      }

      const invalidCommissionSkus = getVariantsWithInvalidCommission(productDetails.variants, region, countries, productDetails.warehouses);
      if (invalidCommissionSkus.length > 0) {
        renderToast(
          toast.TYPE.ERROR,
          `Cannot save: a ${region} commission % over 100 or a ${region} commission amount greater than the ${region} purchase price.`,
          {
            toastId,
            autoClose: false,
          }
        );
        return false;
      }
    }
    return true;
  };

  function save() {
    if (!productDetails || !currentVendor) return;
    const toastId = TEXT_LABELS.SAVE_PRODUCT;

    //condition to check if warehouse added
    if (!warehouseValidated(productDetails)) {
      return;
    }
    //condition to check if regional commission values have a matching regional purchase price
    if (!commissionPricesValidated(productDetails)) {
      return;
    }
    if (saveDisabled) {
      const hasInactiveBadge = !!productDetails?.badge && productDetails.badge.isActive === false;

      renderToast(
        toast.TYPE.ERROR,
        hasInactiveBadge ? PRODUCT_BADGE_INACTIVE : TEXT_LABELS.SAVE_PRODUCT_ERROR,
        {
          toastId,
          autoClose: false,
        }
      );
      setIsErrorHighlight(true)
      return
    }

    if (affiliateFormError) {
      renderToast(
        toast.TYPE.WARNING,
        AFFILIATE_FORM_LABELS.SAVE_FORM_NOT_APPLIED,
        {
          toastId: AFFILIATE_FORM_TOAST_ID,
          autoClose: 8000,
        }
      );
    }

    if (currentVendor) {
      setSaveDisabled(true);
      if (productDetails.name.length > MAX_PRODUCT_NAME_LENGTH) {
        renderToast(
          toast.TYPE.ERROR,
          TEXT_LABELS.PRODUCT_NAME_LENGTH_ERROR,
          {
            toastId,
            autoClose: false,
          }
        );
        setSaveDisabled(false);
        return;
      } else {
        renderToast(
          toast.TYPE.INFO,
          TEXT_LABELS.PRODUCT_SAVING,
          {
            toastId,
            autoClose: false,
          }
        );
        setLoading(true)
        Prod.saveLocalDraft(
          productDetails,
          currentVendor,
          token as string,
          currencies
        )
          .then((res) => {
            setSaveDisabled(false);
            if (res?.message) {
              updateToast(toastId, toast.TYPE.ERROR, res.message, {
                autoClose: 5000,
              });
            } else {
              updateToast(
                toastId,
                toast.TYPE.SUCCESS,
                TEXT_LABELS.DRAFT_SAVED_SUCCESS,
                {
                  autoClose: 5000,
                }
              );
              // Reload both product and variants to ensure IDs are synced in both states
              loadProducts();
              loadProductVariants?.(true); // Reset pagination to show all variants
              if (keepWarehouse) {
                navigate(`${detailsUrl}/variants`)
              } else if (!productDetails.id) {
                navigate(`${getProductTypeConfig(productType).detailUrl}${res.id}`);
              }
            }
          })
          .catch((err) => {
            setSaveDisabled(false);

            updateToast(toastId, toast.TYPE.ERROR, err.message, {
              autoClose: 5000,
            });

            setLoading(false);
          });
      }
    } else {
      renderToast(toast.TYPE.INFO, TEXT_LABELS.SELECT_VENDOR_BEFORE_SAVE);
      return;
    }
  }

  useEffect(() => {
    if (keepWarehouse) {
      save();
      setKeepWarehouse(false);
    }
  }, [keepWarehouse]);

  const validateSubmit = () => {
    if (productDetails && productDetails.warehouses.length === 0) {
      setValidation(true);
    } if (!warehouseValidated(productDetails)) {
      return;
    } if (!commissionPricesValidated(productDetails)) {
      return;
    }
    else {
      setIsSubmitModalOpen(true);
    }
  };

  const previewButton = (
    <Button
      disabled={loading}
      key="preview"
      bgColor="#FFFFFF"
      color={Background.navy}
      borderColor={Shade.shade550}
      onClick={() => navigate(`${detailsUrl}/preview`)}
      margin="5px 0px"
    >
      {TEXT_LABELS.PREVIEW}
    </Button>
  );

  const recommendationEditorTooltip = (
    <TooltipContent>{RECOMMENDATION_STRINGS.EDITOR_OPEN_TOOLTIP}</TooltipContent>
  );

  const saveButton = (
    <Button
      disabled={disabled || recommendationEditorOpen}
      key="save"
      onClick={save}
      bgColor="#FC780B"
      borderColor="#FC780B"
      color="#FFFFFF"
      margin="5px 0px"
    >
      {TEXT_LABELS.SAVE}
    </Button>
  );

  const submitButton = (
    <Button
      margin="5px 4px"
      bgColor="#FC780B"
      borderColor="#FC780B"
      color="#FFFFFF"
      disabled={loading || disabled || recommendationEditorOpen || !warehouseLength || affiliateFormError || productErrorsCount + variantErrorsCount > 0}
      key="submit"
      onClick={validateSubmit}
      {...isDraftStatus ? { permissionCode: typeConfig.permissions.submit } : {}}
    >
      {TEXT_LABELS.SUBMIT}
    </Button>
  );

  const submitButtonWithTooltip =
    (recommendationEditorOpen || (productErrorsCount + variantErrorsCount > 0) || !warehouseLength || affiliateFormError) ? (
      <Tooltip
        content={
          recommendationEditorOpen ? recommendationEditorTooltip : (
            <TooltipContent>
              {affiliateFormError && productErrorsCount + variantErrorsCount === 0 && !!warehouseLength
                ? AFFILIATE_FORM_LABELS.SUBMIT_BLOCKED
                : TEXT_LABELS.PRODUCT_MAY_NOT_PUBLISHED}
            </TooltipContent>
          )
        }
        key="submit"
        size="large"
        hidden={isSubmitReadOnly}
      >
        {submitButton}
      </Tooltip>
    ) : (
      submitButton
    );
  return (
    <>
      {view !== TEXT_LABELS.WAREHOUSES ? previewButton : ''}
      {submitButtonWithTooltip}
      {recommendationEditorOpen ? (
        <Tooltip content={recommendationEditorTooltip} key="save" size="large">
          {saveButton}
        </Tooltip>
      ) : saveButton}
    </>
  );
}
